import { PBPGetResponse, PBPListResponse } from '@percent/admin-dashboard/services/pbpResponses'
import { SingleAdminValidationSubmission } from '@percent/domain/verifications'
import { throttleAsync } from '@percent/utility'
import {
  BulkReleaseHoldsResult,
  CreateValidationSubmissionHostedConfiguration,
  GetHeldValidationSubmissionsProps,
  GetValidationSubmissionConfigurationsProps,
  GetValidationSubmissionProps,
  PatchValidationSubmissionHostedConfiguration,
  UpdateValidationSubmissionConfigurationStatus,
  UpdateValidationSubmissionPurpose,
  UpdateValidationSubmissionSubTags,
  ValidationSubmission,
  ValidationSubmissionConfiguration,
  ValidationSubmissionConfigurationResponse,
  ValidationSubmissionLog,
  HeldValidationSubmission,
  ValidationSubmissionsCapabilities
} from './validationSubmissions.types'

export const validationSubmissionsService = ({ percentClient }: ValidationSubmissionsCapabilities) => ({
  getValidationSubmissionsList: throttleAsync(
    (params: GetValidationSubmissionProps) =>
      percentClient.get<PBPListResponse<ValidationSubmission>>('/v1/admin/validation-submissions', { params }),
    1000,
    { leading: true, trailing: true }
  ),
  getHeldValidationSubmissions: (params: GetHeldValidationSubmissionsProps) =>
    percentClient.get<PBPListResponse<HeldValidationSubmission>>('/v1/admin/validation-submissions/holds', { params }),
  releaseValidationSubmissionHold: ({ id }: { id: string }) =>
    percentClient.post(`/v1/admin/validation-submissions/${id}/hold/release`),
  downloadHeldValidationSubmissionsCsv: async (params: GetHeldValidationSubmissionsProps): Promise<string> => {
    const { data } = await percentClient.get<string>('/v1/admin/validation-submissions/holds/export', {
      params,
      responseType: 'blob'
    })

    return data
  },
  bulkReleaseValidationSubmissionHolds: (csv: string) =>
    percentClient.post<PBPGetResponse<BulkReleaseHoldsResult>>(
      '/v1/admin/validation-submissions/holds/bulk-release',
      csv,
      { headers: { 'Content-Type': 'text/csv' } }
    ),
  getValidationSubmission: ({ id }: { id: string }) =>
    percentClient.get<PBPGetResponse<SingleAdminValidationSubmission>>(`/v1/admin/validation-submissions/${id}`),
  getValidationSubmissionLogs: ({ id }: { id: string }) =>
    percentClient.get<PBPGetResponse<ValidationSubmissionLog[]>>(`/v1/admin/validation-submissions/${id}/logs`),
  getValidationSubmissionConfigurations: ({ partnerId, ...params }: GetValidationSubmissionConfigurationsProps) =>
    percentClient.get<PBPListResponse<ValidationSubmissionConfiguration>>(
      `/v1/admin/partners/${partnerId}/validation-submission-configurations`,
      {
        params
      }
    ),
  getValidationSubmissionConfiguration: ({ id }: { id?: string | null }) =>
    percentClient.get<PBPGetResponse<ValidationSubmissionConfigurationResponse>>(
      `/v1/admin/validation-submission-configurations/${id}`
    ),
  createValidationSubmission: (data: CreateValidationSubmissionHostedConfiguration) =>
    percentClient.post<PBPGetResponse<ValidationSubmissionConfigurationResponse>>(
      `/v1/admin/validation-submission-hosted-configuration`,
      data
    ),
  patchValidationSubmission: ({ id, ...data }: PatchValidationSubmissionHostedConfiguration) =>
    percentClient.patch<PBPGetResponse<ValidationSubmissionConfigurationResponse>>(
      `/v1/admin/validation-submission-configurations/${id}`,
      data
    ),
  updateValidationSubmissionConfigurationStatus: ({ id, deprecated }: UpdateValidationSubmissionConfigurationStatus) =>
    percentClient.patch<PBPGetResponse<ValidationSubmissionConfigurationResponse>>(
      `/v1/admin/validation-submission-configurations/${id}`,
      { deprecated }
    ),
  updateValidationSubmissionPurpose: ({ id, purposes }: UpdateValidationSubmissionPurpose) =>
    percentClient.patch<PBPGetResponse<ValidationSubmissionConfigurationResponse>>(
      `/v1/admin/validation-submission-configurations/${id}`,
      { purposes }
    ),
  updateValidationSubmissionSubTags: ({ id, ...data }: UpdateValidationSubmissionSubTags) =>
    percentClient.put<PBPGetResponse<ValidationSubmissionConfigurationResponse>>(
      `/v1/admin/validation-submission-configuration/${id}/subtags`,
      data
    ),
  duplicateValidationSubmission: ({ configurationId }: { configurationId: string }) =>
    percentClient.post<PBPGetResponse<ValidationSubmissionConfigurationResponse>>(
      `/v1/admin/validation-submission-configurations/${configurationId}/duplicate`
    )
})
